import React, { useMemo, useState } from 'react';

import { Helmet } from 'react-helmet';
import { Link as RouterLink } from 'react-router-dom';

import { CheckCircle as ActiveIcon, CancelOutlined as InactiveIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Container,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import type { definitions } from '@luxuryescapes/contract-svc-promo';

import PageHeader from '~/components/Common/Elements/PageHeader';
import Spinner from '~/components/Common/Spinner';
import usePromoMeta from '~/components/Promo/hooks/usePromoMeta';

import { toPromoBrand } from '~/consts/promoBrand';

import { useGetVouchersByIds } from '~/hooks/apis/promo/useGetVouchersByIds';
import { useListVoucherAutoAssignmentRules } from '~/hooks/apis/promo/useListVoucherAutoAssignmentRules';
import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentTenant from '~/hooks/useCurrentTenant';

import type { Voucher } from '~/services/PromoService';

import DateFormatterDetailed from '~/utils/dateFormatterDetails';

import { voucherAutoAssignGroupPath } from './voucherAutoAssignRulePaths';

type PromoBrand = definitions['Promo Brands'];
type PromoRegion = definitions['Promo Regions'];

const RULE_TYPE_OPTIONS = ['order-complete'] as const;

const FALLBACK_PROMO_REGIONS: Array<PromoRegion> = ['AU', 'NZ', 'US', 'GB', 'SG', 'HK'];

function voucherFormPagePath(voucherId: string) {
  return `/marketing/vouchers/${voucherId}`;
}

function voucherListCellLabel(voucher: Voucher | undefined, voucherId: string): string {
  const title = voucher?.voucherTitle?.trim();
  if (title) {
    return title;
  }
  const name = voucher?.voucherName?.trim();
  if (name) {
    return name;
  }
  return voucherId;
}

export default function VoucherAutoAssignRuleListPage() {
  const { push } = useAppNavigation();
  const { tenant } = useCurrentTenant();
  const { promoMeta } = usePromoMeta();

  const [voucherIdFilter, setVoucherIdFilter] = useState('');
  const [ruleTypeFilter, setRuleTypeFilter] = useState<string>(RULE_TYPE_OPTIONS[0]);
  const brand: PromoBrand | undefined = toPromoBrand(tenant.brand);
  const [regionFilter, setRegionFilter] = useState<PromoRegion | ''>('');

  const regionSelectCodes = useMemo(() => {
    const groupings = promoMeta?.promo_region_groupings ?? [];
    const codes = new Set<string>();
    groupings.forEach((g) => {
      (g.regionCodes ?? []).forEach((c) => codes.add(c));
    });
    const sorted = Array.from(codes).sort() as Array<PromoRegion>;
    return sorted.length > 0 ? sorted : FALLBACK_PROMO_REGIONS;
  }, [promoMeta?.promo_region_groupings]);
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const listQuery = useMemo(() => {
    const isActive = activeFilter === 'active' ? true : activeFilter === 'inactive' ? false : undefined;
    return {
      page,
      limit: rowsPerPage,
      ...(voucherIdFilter.trim() ? { voucher_id: voucherIdFilter.trim() } : {}),
      ...(ruleTypeFilter.trim() ? { rule_type: ruleTypeFilter.trim() } : {}),
      brand,
      ...(regionFilter.trim() ? { region: regionFilter.trim() } : {}),
      ...(isActive !== undefined ? { is_active: isActive } : {}),
    };
  }, [activeFilter, brand, page, regionFilter, rowsPerPage, ruleTypeFilter, voucherIdFilter]);

  const { data, isLoading, isError, error } = useListVoucherAutoAssignmentRules({ query: listQuery });

  const rules = data?.result.rules ?? [];
  const total = data?.result.total ?? 0;

  const voucherIdsOnPage = useMemo(() => rules.map((r) => r.voucherId), [rules]);

  const { data: voucherById } = useGetVouchersByIds({
    voucherIds: voucherIdsOnPage,
    brand: brand ?? '',
    enabled: rules.length > 0 && !isError && Boolean(brand),
  });

  return (
    <>
      <Helmet>
        <title>Marketing / Voucher auto-assign rules</title>
      </Helmet>
      <Container sx={{ mt: 2 }}>
        <PageHeader title="Voucher auto-assign rules">
          <Box>
            <Button
              variant="contained"
              color="primary"
              onClick={() => push('/marketing/voucher-auto-assign-rules/create')}
            >
              Create rule
            </Button>
          </Box>
        </PageHeader>

        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 2, mb: 2 }} alignItems="flex-end">
          <TextField
            label="Voucher ID"
            value={voucherIdFilter}
            onChange={(e) => setVoucherIdFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 260 }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="vaar-rule-type-filter">Rule type</InputLabel>
            <Select
              labelId="vaar-rule-type-filter"
              label="Rule type"
              value={ruleTypeFilter}
              onChange={(e) => setRuleTypeFilter(e.target.value)}
            >
              {RULE_TYPE_OPTIONS.map((rt) => (
                <MenuItem key={rt} value={rt}>
                  {rt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="vaar-region-filter">Region</InputLabel>
            <Select
              labelId="vaar-region-filter"
              label="Region"
              value={regionFilter}
              onChange={(e) => setRegionFilter(e.target.value as PromoRegion | '')}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {regionSelectCodes.map((code) => (
                <MenuItem key={code} value={code}>
                  {code}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="vaar-active-filter">Active</InputLabel>
            <Select
              labelId="vaar-active-filter"
              label="Active"
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        {isLoading && <Spinner />}
        {isError && (
          <Typography color="error" sx={{ mt: 2 }}>
            {error instanceof Error ? error.message : 'Failed to load rules'}
          </Typography>
        )}

        {!isLoading && !isError && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Rule ID</TableCell>
                  <TableCell>Voucher title</TableCell>
                  <TableCell>Rule type</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell>Regions</TableCell>
                  <TableCell>Assignment group</TableCell>
                  <TableCell>Apply order</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Start</TableCell>
                  <TableCell>End</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.map((row) => (
                  <TableRow key={row.voucherAutoAssignmentRuleId} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, maxWidth: 40 }}>
                      {row.voucherAutoAssignmentRuleId}
                    </TableCell>
                    <TableCell>
                      <Link
                        component={RouterLink}
                        to={voucherFormPagePath(row.voucherId)}
                        underline="hover"
                        sx={{ fontSize: 14 }}
                      >
                        {voucherListCellLabel(voucherById?.get(row.voucherId), row.voucherId)}
                      </Link>
                    </TableCell>
                    <TableCell>{row.ruleType}</TableCell>
                    <TableCell>{row.brand}</TableCell>
                    <TableCell>{row.regions?.join(', ')}</TableCell>
                    <TableCell>
                      {row.assignmentGroup ? (
                        <Link
                          component={RouterLink}
                          to={voucherAutoAssignGroupPath(row.assignmentGroup)}
                          underline="hover"
                          sx={{ fontFamily: 'monospace', fontSize: 12 }}
                        >
                          {row.assignmentGroup}
                        </Link>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{row.applyOrder ?? '-'}</TableCell>
                    <TableCell>{row.isActive ? <ActiveIcon /> : <InactiveIcon />}</TableCell>
                    <TableCell>
                      <DateFormatterDetailed date={row.startAssignmentDate} highlightIfPast="green" />
                    </TableCell>
                    <TableCell>
                      <DateFormatterDetailed date={row.endAssignmentDate} highlightIfPast={true} />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="text"
                        color="primary"
                        onClick={() => push(`/marketing/voucher-auto-assign-rules/${row.voucherAutoAssignmentRuleId}`)}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {rules.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11}>
                      <Typography color="text.secondary">No rules match the current filters.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number.parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </TableContainer>
        )}
      </Container>
    </>
  );
}
